'use client';

import type { ReactNode } from 'react';

import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Avatar from '@mui/material/Avatar';
import Divider from '@mui/material/Divider';
import Skeleton from '@mui/material/Skeleton';
import Typography from '@mui/material/Typography';

import { LINE, RADIUS, SURFACE } from 'src/theme/ui-tokens';

import { Iconify } from 'src/components/iconify';

import { HubInfoTip } from './hub-info-tip';
import { HubBrandAvatar } from './hub-brand-avatar';

/** 다 받았는데 셀 수 없는 값 — 분모가 0 이거나 근거 문서가 없다 */
const NO_VALUE = '—';

/**
 * 허브가 **지금 무엇을 보고 있나** — 이름·뱃지·한 줄 설명·주요 단추, 그리고 그 아래 요약 숫자.
 *
 * 🔑 신원과 요약을 **한 카드**에 둔다 — 둘이 떨어져 있으면 「누구의 숫자인가」가 눈으로 안 묶인다.
 * ⚠️ 숫자는 한 줄로 잘라 둔다(`noWrap`) — 길어져 두 줄이 되면 칸마다 높이가 달라진다.
 */
export function HubEntityCard({
  initial,
  imageUrl,
  logo = false,
  title,
  titleHref,
  badges,
  meta,
  actions,
  below,
  stats,
}: {
  /** 머리글자 — 이름 첫 글자. 얼굴이 없을 때 그린다 · 비우고 `imageUrl` 도 없으면 동그라미를 안 그린다 */
  initial: string;
  /** 계정 얼굴 — 없으면 빈 문자열(머리글자로 떨어진다) */
  imageUrl?: string;
  /** 얼굴이 **브랜드 로고**인가 — 참이면 둥근 네모·흰 바탕·안 자름(`HubBrandAvatar`). 사람 얼굴은 거짓(동그라미) */
  logo?: boolean;
  title: string;
  /**
   * 이름을 누르면 갈 **바깥 주소** — 그 계정의 채널 프로필 같은 것.
   * ⚠️ 비면 그냥 글자다(링크 꼴로 보이지 않는다) — 모르는 채널에 짐작으로 주소를 만들지 않는다.
   */
  titleHref?: string;
  /** 이름 옆 뱃지(채널 같은 것) */
  badges?: ReactNode;
  /** 이름 아래 한 줄 */
  meta?: string;
  /** 오른쪽 단추들 */
  actions?: ReactNode;
  /**
   * 머리줄과 숫자 칸 **사이**에 들어가는 것 — 이 대상의 «신원»(소개·연락처·판별값)이 서는 자리다.
   *
   * 🔑 여기에 두는 까닭은 **카드를 하나 더 세우지 않기 위해서**다. 같은 대상을 설명하는 것이 카드 둘로
   *   갈리면 읽는 사람이 위아래를 오가야 한다.
   * ⚠️ 숫자를 두지 마라 — 요약 숫자는 아래 `stats` 자리다(잣대가 한 줄로 읽혀야 한다).
   */
  below?: ReactNode;
  /**
   * 아래 칸을 채우는 요약 숫자 — `loading` 이면 뼈대, 다 받았는데 `value` 가 `null` 이면 「—」(셀 수 없는 값)다.
   * ⚠️ `loading` 을 안 주면 `value === null` 을 «오는 중»으로 읽는다(값이 비는 일이 없는 칸).
   * `hint` 는 칸 폭에 맞는 짧은 말, `tip` 은 마우스를 올리면 보이는 전체 설명이다(없으면 `hint`) — 값이 없어도 그대로 선다.
   * `info` 는 이름표 옆 ⓘ 툴팁의 글머리 줄이다(셈 · 집계 기준 · 적용 안 되는 필터 — `HubInfoTip`).
   */
  stats: { label: string; value: string | null; loading?: boolean; hint?: string; tip?: string; info?: readonly string[] }[];
}) {
  return (
    <Box sx={{ borderRadius: RADIUS.lg, border: '1px solid', borderColor: LINE.edge, bgcolor: 'background.paper', overflow: 'hidden' }}>
      {/* 폰에서는 단추 묶음이 제목 아래 한 줄로 내려간다(제목 옆에서 비좁아진다) */}
      <Stack direction="row" spacing={1.5} useFlexGap alignItems="center" sx={{ px: { xs: 2, md: 2.5 }, py: 2, flexWrap: { xs: 'wrap', sm: 'nowrap' } }}>
        {(initial || imageUrl) && logo && <HubBrandAvatar src={imageUrl} name={title} size={44} typography="uiTitle" />}
        {(initial || imageUrl) && !logo && (
          <Avatar src={imageUrl || undefined} sx={{ width: 44, height: 44, bgcolor: 'primary.lighter', color: 'primary.dark', typography: 'uiTitle' }}>
            {initial}
          </Avatar>
        )}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {/* 좁은 폭에서는 뱃지가 이름 아래로 내려간다 — 이름이 한 줄을 다 쓰면 말줄임으로 줄어든다 */}
          <Stack direction="row" spacing={1} useFlexGap alignItems="center" sx={{ minWidth: 0, flexWrap: 'wrap', rowGap: 0.5 }}>
            {titleHref ? (
              <Link
                href={titleHref}
                target="_blank"
                rel="noopener"
                underline="none"
                sx={{
                  minWidth: 0,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.5,
                  color: 'text.primary',
                  '&:hover': { color: 'primary.main' },
                  '&:hover .hub-entity__out': { opacity: 1 },
                }}
              >
                <Typography noWrap variant="uiHead">{title}</Typography>
                {/* ⚠️ 표는 **늘 자리를 차지한다** — 마우스를 올릴 때 생기면 이름이 밀린다 */}
                <Iconify className="hub-entity__out" icon="solar:square-top-down-linear" width={14} sx={{ flexShrink: 0, opacity: 0.35, transition: (theme) => theme.transitions.create('opacity', { duration: 120 }) }} />
              </Link>
            ) : (
              <Typography noWrap variant="uiHead">{title}</Typography>
            )}
            {badges}
          </Stack>
          {meta && <Typography noWrap variant="uiBody" color="text.secondary" sx={{ mt: 0.125 }}>{meta}</Typography>}
        </Box>
        {actions && <Stack direction="row" spacing={1} alignItems="center" sx={{ flexShrink: 0, width: { xs: 1, sm: 'auto' } }}>{actions}</Stack>}
      </Stack>

      {below && <Box sx={{ px: 2.5, pb: 2 }}>{below}</Box>}

      <Stack
        direction="row"
        divider={<Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', md: 'block' }, borderColor: LINE.hair }} />}
        /* 폰·태블릿은 2×2 격자(칸 높이는 내용만큼) · 넓은 화면은 한 줄 */
        sx={{ display: { xs: 'grid', md: 'flex' }, gridTemplateColumns: { xs: '1fr 1fr' }, borderTop: '1px solid', borderColor: LINE.hair }}
      >
        {stats.map((item) => (
          <Box key={item.label} sx={{ flex: { md: 1 }, minWidth: 0, px: { xs: 2, md: 2.5 }, py: { xs: 1.5, md: 2 } }}>
            {item.info?.length ? (
              <Stack direction="row" spacing={0.25} alignItems="center" sx={{ minWidth: 0 }}>
                <Typography noWrap variant="uiLabel" color="text.disabled">{item.label}</Typography>
                <HubInfoTip items={item.info} size={14} />
              </Stack>
            ) : (
              <Typography noWrap variant="uiLabel" color="text.disabled">{item.label}</Typography>
            )}
            {(item.loading ?? item.value === null) ? (
              <Skeleton variant="text" width={72} height={26} />
            ) : (
              <Typography noWrap variant="uiStat" color={item.value === null ? 'text.disabled' : undefined}>
                {item.value ?? NO_VALUE}
              </Typography>
            )}
            <Typography noWrap variant="uiMeta" color="text.secondary" title={item.tip ?? item.hint} sx={{ minHeight: 17 }}>{item.hint ?? ''}</Typography>
          </Box>
        ))}
      </Stack>
    </Box>
  );
}

/** 뱃지 하나 — 채널처럼 이름 옆에 붙는 작은 표시 */
export function HubBadge({ label }: { label: string }) {
  return (
    <Chip
      size="small"
      label={label}
      sx={{ height: 19, borderRadius: RADIUS.pill, typography: 'uiLabel', bgcolor: SURFACE.sunken, color: 'text.secondary' }}
    />
  );
}

/**
 * 순위 뱃지 — 이름 옆에 「12위」처럼 붙는다.
 * ⚠️ 줄어들지 않는다(`flexShrink: 0`) — 좁으면 이름이 말줄임으로 줄거나 뱃지가 아래 줄로 내려간다.
 */
export function HubRankBadge({ label }: { label: string }) {
  return (
    <Chip
      size="small"
      label={label}
      sx={{ height: 22, flexShrink: 0, borderRadius: RADIUS.pill, typography: 'uiLabel', fontWeight: 700, bgcolor: 'primary.lighter', color: 'primary.darker' }}
    />
  );
}
